<template>
  <div class="modal fade" id="bulkPriceUpdateModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content bg-dark text-light">
        <div class="modal-header border-bottom border-secondary">
          <h5 class="modal-title d-flex align-items-center gap-2 fs-6 fw-semibold">
            <i class="fas fa-tag text-warning"></i>
            Actualización masiva de precios
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <div class="update-progress mb-4" v-if="currentStep">
            <div class="d-flex justify-content-between mb-2">
              <span class="small">Paso {{ currentStep }} de 3</span>
              <span class="small text-primary">{{ getStepLabel }}</span>
            </div>
            <div class="progress bg-dark">
              <div 
                class="progress-bar bg-primary" 
                :style="{ width: `${(currentStep/3)*100}%` }"
              ></div>
            </div>
          </div>

          <form @submit.prevent="handleSubmit" class="needs-validation">
            <!-- Step 1: Select Update Type -->
            <div v-if="currentStep === 1">
              <h6 class="mb-3 text-primary">Selecciona el alcance de la actualización</h6>
              <div class="btn-group d-flex mb-3">
                <button 
                  v-for="type in updateTypes"
                  :key="type.value"
                  type="button"
                  class="btn"
                  :class="formData.type === type.value ? 'btn-primary' : 'btn-outline-light'"
                  @click="selectType(type.value)"
                >
                  <i :class="type.icon" class="me-2"></i>
                  {{ type.label }}
                </button>
              </div>

              <div v-if="formData.type === 'category'" class="mb-3 animate__animated animate__fadeIn">
                <label class="form-label">Selecciona la categoría</label>
                <select v-model="formData.value" class="form-select bg-dark text-light border-secondary">
                  <option value="">Seleccionar categoría...</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                    {{ cat.name }}
                  </option>
                </select>
              </div>

              <div v-if="formData.type === 'brand'" class="mb-3 animate__animated animate__fadeIn">
                <label class="form-label">Selecciona la marca</label>
                <select v-model="formData.value" class="form-select bg-dark text-light border-secondary">
                  <option value="">Seleccionar marca...</option>
                  <option v-for="brand in brands" :key="brand" :value="brand">
                    {{ brand }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Step 2: Amount Type and Value -->
            <div v-if="currentStep === 2" class="animate__animated animate__fadeIn">
              <h6 class="mb-3 text-primary">Define el tipo de actualización</h6>
              
              <div class="mb-3">
                <div class="btn-group w-100 mb-3">
                  <button 
                    type="button"
                    class="btn"
                    :class="formData.percentage ? 'btn-outline-light' : 'btn-primary'"
                    @click="formData.percentage = false"
                  >
                    <i class="fas fa-dollar-sign me-2"></i>
                    Monto fijo
                  </button>
                  <button 
                    type="button"
                    class="btn"
                    :class="formData.percentage ? 'btn-primary' : 'btn-outline-light'"
                    @click="formData.percentage = true"
                  >
                    <i class="fas fa-percent me-2"></i>
                    Porcentaje
                  </button>
                </div>

                <div class="input-group">
                  <span class="input-group-text bg-dark text-light border-secondary">
                    <i :class="formData.percentage ? 'fas fa-percent' : 'fas fa-dollar-sign'"></i>
                  </span>
                  <input 
                    type="number" 
                    class="form-control bg-dark text-light border-secondary"
                    v-model="formData.amount"
                    :placeholder="formData.percentage ? 'Ingrese porcentaje' : 'Ingrese monto'"
                    step="0.01"
                    required
                  />
                </div>
              </div>
            </div>

            <!-- Step 3: Operation Type and Confirmation -->
            <div v-if="currentStep === 3" class="animate__animated animate__fadeIn">
              <h6 class="mb-3 text-primary">Confirma la operación</h6>
              
              <div class="btn-group w-100 mb-4">
                <button 
                  type="button"
                  class="btn"
                  :class="formData.operation === 'increase' ? 'btn-success' : 'btn-outline-light'"
                  @click="formData.operation = 'increase'"
                >
                  <i class="fas fa-plus-circle me-2"></i>
                  Aumentar
                </button>
                <button 
                  type="button"
                  class="btn"
                  :class="formData.operation === 'decrease' ? 'btn-danger' : 'btn-outline-light'"
                  @click="formData.operation = 'decrease'"
                >
                  <i class="fas fa-minus-circle me-2"></i>
                  Disminuir
                </button>
              </div>

              <div class="summary-card p-3 border border-secondary rounded">
                <h6 class="text-primary mb-3">Resumen de la actualización</h6>
                <ul class="list-unstyled mb-0">
                  <li class="mb-2">
                    <i class="fas fa-layer-group me-2 text-muted"></i>
                    <strong>Alcance:</strong> {{ getUpdateTypeSummary }}
                  </li>
                  <li class="mb-2">
                    <i class="fas fa-calculator me-2 text-muted"></i>
                    <strong>Modificación:</strong> {{ getModificationSummary }}
                  </li>
                  <li>
                    <i class="fas fa-arrow-right me-2 text-muted"></i>
                    <strong>Operación:</strong> 
                    <span :class="formData.operation === 'increase' ? 'text-success' : 'text-danger'">
                      {{ formData.operation === 'increase' ? 'Aumentar' : 'Disminuir' }}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </form>
        </div>

        <div class="modal-footer border-top border-secondary">
          <button 
            v-if="currentStep > 1"
            type="button" 
            class="btn btn-outline-light" 
            @click="currentStep--"
          >
            <i class="fas fa-arrow-left me-2"></i>
            Anterior
          </button>
          
          <button 
            v-if="currentStep < 3"
            type="button" 
            class="btn btn-primary" 
            @click="nextStep"
            :disabled="!canProceed"
          >
            Siguiente
            <i class="fas fa-arrow-right ms-2"></i>
          </button>
          
          <button 
            v-if="currentStep === 3"
            type="button" 
            class="btn btn-success" 
            @click="handleSubmit"
            :disabled="isUpdating"
          >
            <span v-if="isUpdating" class="spinner-border spinner-border-sm me-2"></span>
            {{ isUpdating ? 'Actualizando...' : 'Confirmar actualización' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineEmits, defineExpose } from 'vue';
import { Modal } from 'bootstrap';
import { useToast } from 'vue-toastification';
import { getCategories, getBrands } from '@/services/products';
import apiClient from '@/services/apiClient';
import 'animate.css/animate.min.css';  // Update animate.css import

const toast = useToast();
let modal = null;

const currentStep = ref(1);
const isUpdating = ref(false);
const categories = ref([]);
const brands = ref([]);

const formData = ref({
  type: 'all',
  value: '',
  amount: '',
  operation: 'increase',
  percentage: false
});

const updateTypes = [
  { value: 'all', label: 'Todos', icon: 'fas fa-globe' },
  { value: 'category', label: 'Categoría', icon: 'fas fa-folder' },
  { value: 'brand', label: 'Marca', icon: 'fas fa-tag' }
];

const getStepLabel = computed(() => {
  switch (currentStep.value) {
    case 1: return 'Selección de productos';
    case 2: return 'Tipo de actualización';
    case 3: return 'Confirmación';
    default: return '';
  }
});

const canProceed = computed(() => {
  if (currentStep.value === 1) {
    if (formData.value.type === 'all') return true;
    return formData.value.value !== '';
  }
  if (currentStep.value === 2) {
    return formData.value.amount !== '';
  }
  return true;
});

const getUpdateTypeSummary = computed(() => {
  if (formData.value.type === 'all') return 'Todos los productos';
  if (formData.value.type === 'category') {
    const category = categories.value.find(c => c.id === formData.value.value);
    return `Categoría: ${category?.name || ''}`;
  }
  return `Marca: ${formData.value.value}`;
});

const getModificationSummary = computed(() => {
  if (!formData.value.amount) return '';
  if (formData.value.percentage) {
    return `${formData.value.amount}%`;
  }
  return `$${formData.value.amount}`;
});

const selectType = (type) => {
  formData.value.type = type;
  formData.value.value = '';
};

const nextStep = () => {
  if (canProceed.value) {
    currentStep.value++;
  }
};

const updatePrices = async () => {
  try {
    isUpdating.value = true;
    const response = await apiClient.put('/products/bulk-price-update/', formData.value);
    toast.success(response.data.message);
    modal.hide();
    emit('update-complete');
  } catch (error) {
    toast.error(error.response?.data?.error || 'Error al actualizar los precios');
  } finally {
    isUpdating.value = false;
  }
};

const handleSubmit = async () => {
  if (currentStep.value < 3) {
    nextStep();
    return;
  }
  
  if (!formData.value.amount) {
    toast.error('Por favor ingrese un monto válido');
    return;
  }

  await updatePrices();
};

const loadCategories = async () => {
  try {
    const data = await getCategories();
    categories.value = data;
  } catch (error) {
    console.error('Error loading categories:', error);
  }
};

const loadBrands = async () => {
  try {
    brands.value = await getBrands();
  } catch (error) {
    console.error('Error loading brands:', error);
    toast.error('Error al cargar las marcas');
  }
};

const resetForm = () => {
  formData.value = {
    type: 'all',
    value: '',
    amount: '',
    operation: 'increase',
    percentage: false
  };
  currentStep.value = 1;
};

onMounted(async () => {
  modal = new Modal(document.getElementById('bulkPriceUpdateModal'));
  await Promise.all([loadCategories(), loadBrands()]);
  
  // Reset form when modal is hidden
  document.getElementById('bulkPriceUpdateModal').addEventListener('hidden.bs.modal', resetForm);
});

defineExpose({
  show: () => modal.show()
});

const emit = defineEmits(['update-complete']);
</script>

<style scoped>
.modal-content {
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.form-control:focus, .form-select:focus {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25);
}

.progress {
  height: 0.5rem;
  border-radius: 1rem;
}

.progress-bar {
  transition: width 0.3s ease;
}

.btn-group .btn {
  flex: 1;
}

.summary-card {
  background: rgba(255, 255, 255, 0.05);
}

.animate__animated {
  animation-duration: 0.3s;
}

.form-select option {
  background-color: var(--bs-dark);
  color: var(--bs-light);
}
</style>
